import React from 'react';
import { Link2, FileText, History, LayoutDashboard, Moon, Sun, Award, Mail, X, User, LogIn, GitCompare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { useApp } from '@/context/AppContext';
import { useAuth } from '@/context/AuthContext';
import { cn } from '@/lib/utils';
import veriLinkLogo from '@/assets/verilink-logo.png';

const navItems = [
  { id: 'link', label: 'Link Verification', icon: Link2 },
  { id: 'article', label: 'Article Check', icon: FileText },
  { id: 'email', label: 'Email Checker', icon: Mail },
  { id: 'compare', label: 'Article Compare', icon: GitCompare },
  { id: 'phishing-lab', label: 'Phishing Lab', icon: Award },
  { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
  { id: 'history', label: 'History', icon: History },
];

export const MobileNav = ({ onClose, onShowAuth }) => {
  const { activeView, setActiveView, darkMode, toggleDarkMode, stats, earnedBadges } = useApp();
  const { isAuthenticated, user } = useAuth();

  const go = (id) => { setActiveView(id); onClose(); };

  return (
    <>
      <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={onClose} />
      <div className="fixed inset-y-0 left-0 w-[280px] max-w-[85vw] z-50 lg:hidden glass-card border-r border-border/50 flex flex-col animate-slide-in">
        <div className="p-4 border-b border-border/30 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src={veriLinkLogo} alt="VeriLink" className="w-8 h-8 rounded-lg object-contain" />
            <span className="font-display text-lg font-bold">VeriLink</span>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose} className="h-8 w-8"><X className="w-4 h-4" /></Button>
        </div>

        {isAuthenticated ? (
          <div className="p-3 border-b border-border/30">
            <button onClick={() => go('account')} className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-muted">
              <div className="w-10 h-10 rounded-full gradient-primary flex items-center justify-center flex-shrink-0">
                <span className="text-lg font-bold text-primary-foreground">{user?.email[0].toUpperCase()}</span>
              </div>
              <p className="font-medium text-sm truncate">{user?.email}</p>
            </button>
          </div>
        ) : (
          <div className="p-3 border-b border-border/30">
            <Button variant="gradient" onClick={() => { onClose(); onShowAuth?.(); }} className="w-full h-12" data-testid="mobile-login-btn">
              <LogIn className="w-4 h-4 mr-2" />Sign In / Register
            </Button>
          </div>
        )}

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const isActive = activeView === item.id;
            return (
              <button key={item.id} onClick={() => go(item.id)}
                className={cn("w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all min-h-[48px]",
                  isActive ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-muted text-foreground")}>
                <Icon className={cn("w-5 h-5 flex-shrink-0", isActive && "text-primary-foreground")} />
                <span className={cn("font-medium text-sm", isActive && "text-primary-foreground")}>{item.label}</span>
              </button>
            );
          })}
          {isAuthenticated && (
            <button onClick={() => go('account')}
              className={cn("w-full flex items-center gap-3 px-3 py-3 rounded-xl transition-all min-h-[48px]",
                activeView === 'account' ? "bg-primary text-primary-foreground shadow-md" : "hover:bg-muted text-foreground")}>
              <User className={cn("w-5 h-5 flex-shrink-0", activeView === 'account' && "text-primary-foreground")} />
              <span className={cn("font-medium text-sm", activeView === 'account' && "text-primary-foreground")}>My Account</span>
            </button>
          )}
        </nav>

        <div className="p-3 border-t border-border/30 space-y-2">
          <div className="flex items-center justify-between px-2">
            <span className="text-xs text-muted-foreground">Verified</span>
            <Badge variant="secondary" className="font-mono text-xs">{stats.totalVerifications}</Badge>
          </div>
          <div className="flex items-center justify-between px-2">
            <span className="text-xs text-muted-foreground">Streak</span>
            <Badge variant="safe" className="font-mono text-xs">&#128293; {stats.streak}d</Badge>
          </div>
        </div>

        <div className="p-3 border-t border-border/30">
          <Button variant="ghost" onClick={toggleDarkMode} className="w-full justify-start gap-3 min-h-[48px]">
            {darkMode ? <Sun className="w-5 h-5 text-warning" /> : <Moon className="w-5 h-5 text-primary" />}
            <span>{darkMode ? 'Light Mode' : 'Dark Mode'}</span>
          </Button>
        </div>
      </div>
    </>
  );
};

export default MobileNav;
